import React from 'react';
import {
  Button,
  Input,
  Message,
  SelectPicker,
  Table,
  Tooltip,
  Whisper,
  useToaster,
} from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import DeleteIcon from '@rsuite/icons/Trash';
import CheckIcon from '@rsuite/icons/Check';
import CloseIcon from '@rsuite/icons/Close';
import * as blogPostsApi from '../../api/blogPosts';
import * as blogCategoriesApi from '../../api/blogCategories';
import PageLayout from '../../common/PageLayout';
import ColumnsButton from '../../common/ColumnsButton';
import FilterPanel, { FilterField } from '../../common/FilterPanel';
import { formatDateTime, getRowClassName } from '../../common/formatters';
import { renderRowNumberColumn } from '../../common/RowNumberColumn';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import TablePagination from '../../common/TablePagination';
import { useFormErrors } from '../../common/useFormErrors';
import { useTableColumns } from '../../common/useTableColumns';
import { useConfirm } from '../../common/useConfirm';
import { useResourceList } from '../../common/useResourceList';
import CreateBlogPostModal from './CreateBlogPostModal';
import EditBlogPostModal from './EditBlogPostModal';
import { SortType } from 'rsuite-table';

const { Column, HeaderCell, Cell } = Table;

type BlogPost = {
  id: string;
  slug: string;
  title: string;
  titleEn?: string | null;
  titleDe?: string | null;
  content: string;
  contentEn?: string | null;
  contentDe?: string | null;
  active?: boolean | null;
  thumbnail?: string | null;
  image?: string | null;
  postedAt?: number | null;
  createdAt?: number | null;
  updatedAt?: number | null;
  categoryId: string;
  categoryTitle: string;
  categorySlug: string;
};

type BlogCategory = {
  id: string;
  slug: string;
  title: string;
};

const TABLE_COLUMNS = [
  {
    value: 'active',
    label: 'Активний',
    display: true,
    flexGrow: 1,
    minWidth: 120,
  },
  {
    value: 'title',
    label: 'Заголовок',
    display: true,
    flexGrow: 3,
    minWidth: 220,
  },
  { value: 'slug', label: 'Slug', display: true, flexGrow: 2, minWidth: 160 },
  {
    value: 'categoryTitle',
    label: 'Категорія',
    display: true,
    flexGrow: 2,
    minWidth: 160,
  },
  {
    value: 'postedAt',
    label: 'Опубліковано',
    display: true,
    flexGrow: 1,
    minWidth: 140,
  },
  {
    value: 'thumbnail',
    label: 'Thumbnail',
    display: false,
    flexGrow: 2,
    minWidth: 180,
  },
  {
    value: 'image',
    label: 'Image',
    display: false,
    flexGrow: 2,
    minWidth: 180,
  },
  {
    value: 'createdAt',
    label: 'Створено',
    display: false,
    flexGrow: 1,
    minWidth: 120,
  },
];

export default function BlogPosts() {
  const toaster = useToaster();
  const [confirm, ConfirmDialog] = useConfirm();
  const [search, setSearch] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('');
  const [categories, setCategories] = React.useState<BlogCategory[]>([]);

  const [tableColumns, , toggleColumn] = useTableColumns(
    TABLE_COLUMNS,
    'blogPosts:tableColumns'
  );

  const [sortColumn, setSortColumn] =
    React.useState<keyof BlogPost>('postedAt');
  const [sortType, setSortType] = React.useState<SortType>('desc');

  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const [modalPostId, setModalPostId] = React.useState('');
  const [modalTitle, setModalTitle] = React.useState('');
  const [modalTitleEn, setModalTitleEn] = React.useState('');
  const [modalTitleDe, setModalTitleDe] = React.useState('');
  const [modalSlug, setModalSlug] = React.useState('');
  const [modalCategoryId, setModalCategoryId] = React.useState('');
  const [modalPostedAt, setModalPostedAt] = React.useState<Date | null>(null);
  const [modalActive, setModalActive] = React.useState(true);
  const [modalThumbnail, setModalThumbnail] = React.useState('');
  const [modalImage, setModalImage] = React.useState('');
  const [modalContent, setModalContent] = React.useState('');
  const [modalContentEn, setModalContentEn] = React.useState('');
  const [modalContentDe, setModalContentDe] = React.useState('');

  const {
    errorSummary: updateErrors,
    setApiError: setUpdateError,
    clearErrors: clearUpdateErrors,
  } = useFormErrors({
    fallbackMessage: 'Не вдалося оновити пост. Спробуйте пізніше',
    resetOn: [modalPostId],
  });

  const loadCategories = React.useCallback(async () => {
    try {
      const response = await blogCategoriesApi.list();
      setCategories(response.categories || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const list = useResourceList<BlogPost>({
    fetch: ({ page, perPage }) =>
      blogPostsApi.list(
        page,
        perPage,
        search || undefined,
        categoryFilter || undefined
      ),
    filters: [search, categoryFilter],
  });

  React.useEffect(() => {
    loadCategories().catch(console.error);
  }, [loadCategories]);

  const handleModalClose = () => {
    setModalPostId('');
    setModalTitle('');
    setModalTitleEn('');
    setModalTitleDe('');
    setModalSlug('');
    setModalCategoryId('');
    setModalPostedAt(null);
    setModalActive(true);
    setModalThumbnail('');
    setModalImage('');
    setModalContent('');
    setModalContentEn('');
    setModalContentDe('');
  };

  const normalizeTimestamp = (value?: number | null) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    return 0;
  };

  const sortedPosts = React.useMemo(() => {
    if (!sortColumn || !sortType) {
      return list.data;
    }

    const sorted = [...list.data];
    sorted.sort((a, b) => {
      let result = 0;
      if (sortColumn === 'active') {
        const left = a.active ? 1 : 0;
        const right = b.active ? 1 : 0;
        result = left - right;
      } else if (sortColumn === 'title') {
        result = a.title.localeCompare(b.title, 'uk', { sensitivity: 'base' });
      } else if (sortColumn === 'slug') {
        result = a.slug.localeCompare(b.slug, 'uk', { sensitivity: 'base' });
      } else if (sortColumn === 'categoryTitle') {
        result = a.categoryTitle.localeCompare(b.categoryTitle, 'uk', {
          sensitivity: 'base',
        });
      } else if (sortColumn === 'postedAt') {
        result =
          normalizeTimestamp(a.postedAt) - normalizeTimestamp(b.postedAt);
      } else if (sortColumn === 'createdAt') {
        result =
          normalizeTimestamp(a.createdAt) - normalizeTimestamp(b.createdAt);
      } else if (sortColumn === 'thumbnail') {
        result = (a.thumbnail ?? '').localeCompare(b.thumbnail ?? '', 'uk', {
          sensitivity: 'base',
        });
      } else if (sortColumn === 'image') {
        result = (a.image ?? '').localeCompare(b.image ?? '', 'uk', {
          sensitivity: 'base',
        });
      }

      return sortType === 'asc' ? result : -result;
    });

    return sorted;
  }, [sortColumn, sortType, list.data]);

  const setRowDataEdition = (rowData: BlogPost) => {
    setModalPostId(rowData.id);
    setModalTitle(rowData.title);
    setModalTitleEn(rowData.titleEn || '');
    setModalTitleDe(rowData.titleDe || '');
    setModalSlug(rowData.slug);
    setModalCategoryId(rowData.categoryId);
    setModalPostedAt(rowData.postedAt ? new Date(rowData.postedAt) : null);
    setModalActive(rowData.active ?? true);
    setModalThumbnail(rowData.thumbnail || '');
    setModalImage(rowData.image || '');
    setModalContent(rowData.content || '');
    setModalContentEn(rowData.contentEn || '');
    setModalContentDe(rowData.contentDe || '');
  };

  const updatePost = async () => {
    if (!modalPostId) {
      return;
    }

    clearUpdateErrors();
    try {
      await blogPostsApi.update(
        modalPostId,
        modalTitle,
        modalTitleEn,
        modalTitleDe,
        modalSlug,
        modalContent,
        modalContentEn,
        modalContentDe,
        modalCategoryId,
        modalPostedAt ? modalPostedAt.getTime() : undefined,
        modalThumbnail || undefined,
        modalImage || undefined,
        modalActive
      );
      handleModalClose();
      list.reload();
    } catch (err: any) {
      const errorMessage = setUpdateError(err)[0];
      toaster.push(
        <Message type="error" showIcon closable>
          {errorMessage}
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
    }
  };

  const categoryOptions = React.useMemo(
    () =>
      categories.map(category => ({
        label: category.title,
        value: category.id,
      })),
    [categories]
  );

  const firstVisibleColumnValue = React.useMemo(
    () => tableColumns.find(column => column.display !== false)?.value,
    [tableColumns]
  );

  const renderColumn = (column: (typeof TABLE_COLUMNS)[0]) => {
    if (column.display === false) {
      return null;
    }

    const commonProps = {
      key: column.value,
      flexGrow: column.flexGrow ?? 1,
      minWidth: column.minWidth || 80,
      sortable: true,
      fixed:
        column.value === firstVisibleColumnValue
          ? ('left' as const)
          : undefined,
    };

    switch (column.value) {
      case 'active':
        return (
          <Column {...commonProps}>
            <HeaderCell>Активний</HeaderCell>
            <Cell
              dataKey="active"
              renderCell={(content: unknown) => {
                const value = content;
                const isActive =
                  value === true ||
                  value === 1 ||
                  value === '1' ||
                  value === 'true';
                const isInactive =
                  value === false ||
                  value === 0 ||
                  value === '0' ||
                  value === 'false';
                if (isActive) {
                  return <CheckIcon style={{ color: 'var(--success)' }} />;
                }
                if (isInactive) {
                  return <CloseIcon style={{ color: 'var(--error)' }} />;
                }
                return <span style={{ color: 'var(--muted)' }}>—</span>;
              }}
            />
          </Column>
        );
      case 'title':
        return (
          <Column {...commonProps} fullText>
            <HeaderCell>Заголовок</HeaderCell>
            <Cell dataKey="title" />
          </Column>
        );
      case 'slug':
        return (
          <Column {...commonProps} fullText>
            <HeaderCell>Slug</HeaderCell>
            <Cell dataKey="slug" />
          </Column>
        );
      case 'categoryTitle':
        return (
          <Column {...commonProps} fullText>
            <HeaderCell>Категорія</HeaderCell>
            <Cell dataKey="categoryTitle" />
          </Column>
        );
      case 'postedAt':
        return (
          <Column {...commonProps}>
            <HeaderCell>Опубліковано</HeaderCell>
            <Cell
              dataKey="postedAt"
              renderCell={(content: number) => formatDateTime(content)}
            />
          </Column>
        );
      case 'thumbnail':
        return (
          <Column {...commonProps} fullText>
            <HeaderCell>Thumbnail</HeaderCell>
            <Cell dataKey="thumbnail" />
          </Column>
        );
      case 'image':
        return (
          <Column {...commonProps} fullText>
            <HeaderCell>Image</HeaderCell>
            <Cell dataKey="image" />
          </Column>
        );
      case 'createdAt':
        return (
          <Column {...commonProps}>
            <HeaderCell>Створено</HeaderCell>
            <Cell
              dataKey="createdAt"
              renderCell={(content: number) => formatDateTime(content)}
            />
          </Column>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {ConfirmDialog}
      <PageLayout
        title="Блог"
        actions={
          <>
            <Input
              className="toolbar-search"
              placeholder="Пошук за заголовком або текстом"
              value={search}
              onChange={setSearch}
            />
            <FilterPanel
              activeCount={categoryFilter ? 1 : 0}
              onReset={() => setCategoryFilter('')}
            >
              {filterContainer => (
                <FilterField label="Категорія">
                  <SelectPicker
                    block
                    placeholder="Категорія"
                    data={categoryOptions}
                    value={categoryFilter || null}
                    onChange={value => setCategoryFilter(value || '')}
                    cleanable
                    container={filterContainer}
                    preventOverflow
                  />
                </FilterField>
              )}
            </FilterPanel>
            <ColumnsButton columns={tableColumns} onToggle={toggleColumn} />
            <Button
              appearance="primary"
              onClick={() => setCreateModalOpen(true)}
            >
              Створити
            </Button>
          </>
        }
      >
        <div className="section-card">
          <Table
            wordWrap="break-word"
            loading={list.loading}
            autoHeight={true}
            affixHeader={true}
            affixHorizontalScrollbar={true}
            data={sortedPosts}
            renderLoading={renderTableLoading}
            renderEmpty={renderTableEmpty}
            rowClassName={getRowClassName}
            sortColumn={sortColumn}
            sortType={sortType}
            onSortColumn={(nextColumn, nextType) => {
              setSortColumn(nextColumn as keyof BlogPost);
              setSortType(nextType || 'asc');
            }}
          >
            {renderRowNumberColumn({ page: list.page, perPage: list.perPage })}
            {tableColumns.map(renderColumn)}

            <Column fixed="right" width={100}>
              <HeaderCell>Дії</HeaderCell>
              <Cell style={{ padding: '4px 6px' }}>
                {(rowData: BlogPost) => (
                  <div className="action-icons">
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Редагувати</Tooltip>}
                    >
                      <Button
                        appearance="link"
                        onClick={() => {
                          setRowDataEdition(rowData);
                        }}
                      >
                        <EditIcon />
                      </Button>
                    </Whisper>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Видалити</Tooltip>}
                    >
                      <Button
                        appearance="subtle"
                        className="delete-btn"
                        onClick={async () => {
                          const result = await confirm({
                            message: `Ви впевнені, що хочете видалити пост ${rowData.title}?`,
                            destructive: true,
                            confirmText: 'Видалити',
                          });
                          if (!result) {
                            return;
                          }
                          blogPostsApi
                            .remove(rowData.id)
                            .catch(console.error)
                            .then(() => list.reload());
                        }}
                      >
                        <DeleteIcon />
                      </Button>
                    </Whisper>
                  </div>
                )}
              </Cell>
            </Column>
          </Table>

          <TablePagination
            total={list.totalAmount}
            activePage={list.page}
            limit={list.perPage}
            limitOptions={[15, 30, 50, 100]}
            onChangeLimit={list.setPerPage}
            onChangePage={list.goToPage}
          />
        </div>

        <EditBlogPostModal
          open={modalPostId.length > 0}
          onClose={handleModalClose}
          postId={modalPostId}
          title={modalTitle}
          titleEn={modalTitleEn}
          titleDe={modalTitleDe}
          slug={modalSlug}
          categoryId={modalCategoryId}
          postedAt={modalPostedAt}
          active={modalActive}
          thumbnail={modalThumbnail}
          image={modalImage}
          content={modalContent}
          contentEn={modalContentEn}
          contentDe={modalContentDe}
          categories={categoryOptions}
          setTitle={setModalTitle}
          setTitleEn={setModalTitleEn}
          setTitleDe={setModalTitleDe}
          setSlug={setModalSlug}
          setCategoryId={setModalCategoryId}
          setPostedAt={setModalPostedAt}
          setActive={setModalActive}
          setThumbnail={setModalThumbnail}
          setImage={setModalImage}
          setContent={setModalContent}
          setContentEn={setModalContentEn}
          setContentDe={setModalContentDe}
          onSave={updatePost}
          errorSummary={updateErrors}
        />

        <CreateBlogPostModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          categories={categoryOptions}
          onCreated={() => {
            list.reload();
          }}
        />
      </PageLayout>
    </>
  );
}
